import { FC, useState } from 'react'
import { Box, ButtonBase, Typography, useMediaQuery, useTheme } from '@mui/material'
import { KeyboardArrowRight } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { useIntl } from 'react-intl'

import BackNavigationHeader, { BackNavigationContent } from 'components/BackNavigationHeader'
import {
  UserDetailsAddress,
  UserDetailsIce,
  UserDetailsAddressDuringRental,
  UserDetailsAddressAfterRental
} from 'components/User'
import theme from 'styles/theme'

const UserDetails: FC = () => {
  const { formatMessage } = useIntl()
  const muiTheme = useTheme()
  const isDesktop = useMediaQuery(muiTheme.breakpoints.up('md'))
  const navigate = useNavigate()
  const [detailsView, setDetailsView] = useState<
    'menu' | 'address' | 'addressDuring' | 'addressAfter' | 'ice'
  >('menu')

  switch (detailsView) {
    case 'address':
      return <UserDetailsAddress onBack={() => setDetailsView('menu')} />
    case 'addressDuring':
      return <UserDetailsAddressDuringRental onBack={() => setDetailsView('menu')} />
    case 'addressAfter':
      return <UserDetailsAddressAfterRental onBack={() => setDetailsView('menu')} />
    case 'ice':
      return <UserDetailsIce onBack={() => setDetailsView('menu')} />

    default: {
      const items = [
        {
          key: 'address',
          title: formatMessage({ id: 'userDetails.address', defaultMessage: 'Adres zamieszkania' }),
          onClick: () => setDetailsView('address')
        },
        {
          key: 'addressDuring',
          title: formatMessage({
            id: 'userDetails.addressDuring',
            defaultMessage: 'Adres do doręczeń'
          }),
          subtitle: formatMessage({
            id: 'userDetails.addressDuring2',
            defaultMessage: 'W trakcie trwania umowy'
          }),
          onClick: () => setDetailsView('addressDuring')
        },
        {
          key: 'addressAfter',
          title: formatMessage({
            id: 'userDetails.addressDuring',
            defaultMessage: 'Adres do doręczeń'
          }),
          subtitle: formatMessage({
            id: 'userDetails.addressDuring3',
            defaultMessage: 'Po zakończeniu umowy'
          }),
          onClick: () => setDetailsView('addressAfter')
        },
        {
          key: 'ice',
          title: 'ICE',
          onClick: () => setDetailsView('ice')
        },
        {
          key: 'bank',
          title: formatMessage({ id: 'userDetails.bankAccount', defaultMessage: 'Dane bankowe' }),
          onClick: () => navigate('/user/bank-account')
        }
      ]

      const content = (
        <>
          {isDesktop && (
            <Typography
              sx={{ fontSize: '18px', fontWeight: 800, color: theme.r4r.navy, mb: '28px' }}>
              {formatMessage({ id: 'userDetails.label', defaultMessage: 'Dane kontaktowe' })}
            </Typography>
          )}
          <Box>
            {items.map((item, index) => (
              <ButtonBase
                key={item.key}
                onClick={item.onClick}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                  textAlign: 'left',
                  backgroundColor: '#fff',
                  px: '24px',
                  py: item.subtitle ? '20px' : '21px',
                  borderTop: index === 0 ? `1px solid ${theme.r4r.greyLight}` : 'none',
                  borderBottom: `1px solid ${theme.r4r.greyLight}`,
                  ':hover': { backgroundColor: 'rgba(31, 31, 115, 0.04)' }
                }}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    sx={{ fontSize: '15px', fontWeight: 600, color: theme.r4r.ink, lineHeight: 1.2 }}>
                    {item.title}
                  </Typography>
                  {!!item.subtitle && (
                    <Typography sx={{ fontSize: '12px', color: theme.r4r.grey, mt: '2px' }}>
                      {item.subtitle}
                    </Typography>
                  )}
                </Box>
                <KeyboardArrowRight sx={{ color: theme.r4r.grey, fontSize: '18px', ml: '16px' }} />
              </ButtonBase>
            ))}
          </Box>
        </>
      )

      if (isDesktop) {
        return content
      }

      return (
        <>
          <BackNavigationHeader
            label={formatMessage({ id: 'userDetails.label', defaultMessage: 'Dane kontaktowe' })}
          />
          <BackNavigationContent>{content}</BackNavigationContent>
        </>
      )
    }
  }
}

export default UserDetails
